// src/pages/Admin/AdminPayments.jsx
import React, { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { Card, Badge, Spinner, Button, Modal } from '../../components/ui';
import {
  CreditCard, Search, RefreshCw, Building, Users,
  CheckCircle, XCircle, Clock, AlertCircle,
  DollarSign, Wallet, TrendingUp, Calendar, Filter,
  Eye, Phone, Mail, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight
} from 'lucide-react';
import './Admin.css';

const AdminPayments = () => {
  const [transactions, setTransactions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrg, setFilterOrg] = useState('all');
  const [stats, setStats] = useState(null);
  const [orgTransactions, setOrgTransactions] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [loadingOrgTx, setLoadingOrgTx] = useState(false);
  const [showRegistrationFees, setShowRegistrationFees] = useState(false);
  const [registrationFees, setRegistrationFees] = useState([]);
  const [loadingRegFees, setLoadingRegFees] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  
  const { request } = useApi();

  useEffect(() => {
    fetchAllData();
  }, [currentPage, pageSize, filterType, filterStatus, filterOrg]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('size', pageSize);
      if (filterType !== 'all') params.append('type', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterOrg !== 'all') params.append('orgId', filterOrg);

      const [txResponse, orgData, statsData] = await Promise.all([
        request('get', `/api/admin/payments/transactions?${params.toString()}`).catch(() => ({ content: [], totalPages: 0, totalElements: 0 })),
        request('get', '/api/admin/organizations').catch(() => []),
        request('get', '/api/admin/payments/stats').catch(() => ({})),
      ]);

      setTransactions(txResponse.content || []);
      setTotalPages(txResponse.totalPages || 0);
      setTotalElements(txResponse.totalElements || 0);
      setOrganizations(orgData || []);
      setStats(statsData || {});
    } catch (err) {
      console.error('Failed to fetch payment data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgTransactions = async (orgId) => {
    setLoadingOrgTx(true);
    try {
      const data = await request('get', `/api/admin/payments/org/${orgId}/transactions`);
      setOrgTransactions(data.content || []);
      const org = organizations.find(o => o.id === orgId);
      setSelectedOrg(org);
      setShowOrgModal(true);
    } catch (err) {
      console.error('Failed to fetch org transactions:', err);
    } finally {
      setLoadingOrgTx(false);
    }
  };

  const fetchRegistrationFees = async () => {
    setLoadingRegFees(true);
    try {
      const data = await request('get', '/api/admin/payments/registration-fees');
      setRegistrationFees(data || []);
      setShowRegistrationFees(true);
    } catch (err) {
      console.error('Failed to fetch registration fees:', err);
    } finally {
      setLoadingRegFees(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'COMPLETED': <Badge variant="success"><CheckCircle size={12} /> Completed</Badge>,
      'PENDING': <Badge variant="warning"><Clock size={12} /> Pending</Badge>,
      'FAILED': <Badge variant="danger"><XCircle size={12} /> Failed</Badge>,
      'CANCELLED': <Badge variant="default"><XCircle size={12} /> Cancelled</Badge>,
      'PROCESSING': <Badge variant="info"><Clock size={12} /> Processing</Badge>,
    };
    return statusMap[status] || <Badge variant="default">{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (type === 'REGISTRATION') {
      return <Badge variant="info"><Users size={12} /> Registration</Badge>;
    }
    if (type === 'CREDIT_PURCHASE' || type === 'CREDIT') {
      return <Badge variant="success"><DollarSign size={12} /> Credits</Badge>;
    }
    return <Badge variant="default">{type}</Badge>;
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || orgId?.slice(0, 8) || 'Unknown';
  };

  const getOrgStatus = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.status || 'UNKNOWN';
  };

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  const goToFirstPage = () => setCurrentPage(0);
  const goToLastPage = () => setCurrentPage(totalPages - 1);
  const goToNextPage = () => goToPage(currentPage + 1);
  const goToPrevPage = () => goToPage(currentPage - 1);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [filterType, filterStatus, filterOrg]);

  const totalRevenue = stats?.totalRevenue || 0;
  const totalRegistrationFees = stats?.totalRegistrationFees || 0;
  const totalCreditPurchases = stats?.totalCreditPurchases || 0;
  const totalTransactions = stats?.totalTransactions || 0;
  const orgsWithPaidReg = stats?.orgsWithPaidRegistration || 0;
  const totalCreditsSold = stats?.totalCreditsSold || 0;
  const pendingCount = stats?.pendingTransactions || 0;
  const failedCount = stats?.failedTransactions || 0;

  if (loading && currentPage === 0) {
    return (
      <div className="admin-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Payments & Billing</h1>
          <p className="admin-subtitle">Monitor all financial transactions across the platform</p>
        </div>
        <button className="refresh-btn" onClick={fetchAllData}>
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Stats Summary */}
      <div className="admin-stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '24px' }}>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #10B981' }}>
          <div className="admin-stat-icon green">
            <DollarSign size={22} />
          </div>
          <div className="admin-stat-info">
            <div className="admin-stat-value">TZS {totalRevenue.toLocaleString()}</div>
            <div className="admin-stat-label">Total Revenue</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #8B5CF6' }}>
          <div className="admin-stat-icon purple">
            <Users size={22} />
          </div>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{orgsWithPaidReg}</div>
            <div className="admin-stat-label">Paid Registrations</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #F59E0B' }}>
          <div className="admin-stat-icon orange">
            <Wallet size={22} />
          </div>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{totalCreditsSold.toLocaleString()}</div>
            <div className="admin-stat-label">Credits Sold</div>
          </div>
        </div>
        <div className="admin-stat-card" style={{ borderLeft: '4px solid #3B82F6' }}>
          <div className="admin-stat-icon blue">
            <CreditCard size={22} />
          </div>
          <div className="admin-stat-info">
            <div className="admin-stat-value">{totalTransactions}</div>
            <div className="admin-stat-label">Total Transactions</div>
          </div>
        </div>
      </div>

      {/* Quick Status Cards */}
      <div className="quick-actions-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: '24px' }}>
        <div className="quick-action-card clickable" onClick={fetchRegistrationFees} style={{ cursor: 'pointer' }}>
          <div className="quick-action-icon green">
            <CheckCircle size={20} />
          </div>
          <div>
            <h4>Registration Fees</h4>
            <p className="quick-action-value">TZS {totalRegistrationFees.toLocaleString()}</p>
            <span style={{ fontSize: '11px', color: '#64748B' }}>{orgsWithPaidReg} organizations</span>
          </div>
        </div>
        <div className="quick-action-card">
          <div className="quick-action-icon blue">
            <TrendingUp size={20} />
          </div>
          <div>
            <h4>Credit Purchases</h4>
            <p className="quick-action-value">TZS {totalCreditPurchases.toLocaleString()}</p>
            <span style={{ fontSize: '11px', color: '#64748B' }}>{totalCreditsSold.toLocaleString()} credits</span>
          </div>
        </div>
        <div className="quick-action-card">
          <div className="quick-action-icon orange">
            <Clock size={20} />
          </div>
          <div>
            <h4>Pending Payments</h4>
            <p className="quick-action-value">{pendingCount}</p>
            <span style={{ fontSize: '11px', color: '#64748B' }}>Awaiting confirmation</span>
          </div>
        </div>
        <div className="quick-action-card">
          <div className="quick-action-icon red">
            <AlertCircle size={20} />
          </div>
          <div>
            <h4>Failed Payments</h4>
            <p className="quick-action-value">{failedCount}</p>
            <span style={{ fontSize: '11px', color: '#64748B' }}>Need attention</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="admin-filters">
        <div className="search-wrapper" style={{ maxWidth: '100%', flex: '1' }}>
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by organization or reference..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <Filter size={18} className="filter-icon" />
          <select 
            className="filter-select" 
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="REGISTRATION">Registration</option>
            <option value="CREDIT_PURCHASE">Credits</option>
          </select>
        </div>
        <div className="filter-group">
          <Filter size={18} className="filter-icon" />
          <select 
            className="filter-select" 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="COMPLETED">Completed</option>
            <option value="PENDING">Pending</option>
            <option value="PROCESSING">Processing</option>
            <option value="FAILED">Failed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>
        <div className="filter-group">
          <Filter size={18} className="filter-icon" />
          <select 
            className="filter-select" 
            value={filterOrg}
            onChange={(e) => setFilterOrg(e.target.value)}
          >
            <option value="all">All Organizations</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <Card className="admin-table-container" padding="none">
        <div className="table-header">
          <h3 className="table-title">All Transactions</h3>
          <div className="table-info">
            {totalElements} transactions · Page {currentPage + 1} of {totalPages || 1}
          </div>
        </div>
        <div className="table-responsive">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Organization</th>
                <th>Type</th>
                <th>Credits</th>
                <th>Amount</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr className="empty-row">
                  <td colSpan={9}>
                    <div className="empty-state">
                      <CreditCard size={48} className="empty-icon" />
                      <h3>No transactions found</h3>
                      <p>Try adjusting your search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => (
                  <tr key={tx.id}>
                    <td className="tx-reference">{tx.sourceReference || tx.id?.slice(0, 12)}</td>
                    <td>
                      <div className="org-name">{getOrgName(tx.organizationId)}</div>
                      <div className="org-status-text" style={{ fontSize: '10px', color: '#94A3B8' }}>
                        {getOrgStatus(tx.organizationId)}
                      </div>
                    </td>
                    <td>{getTypeBadge(tx.transactionType)}</td>
                    <td>
                      {tx.credits != null ? (
                        <span className="credits-amount">
                          {tx.credits.toLocaleString()}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="tx-amount">
                      <span className="font-semibold">TZS {Number(tx.amount).toLocaleString()}</span>
                    </td>
                    <td className="tx-channel">{tx.channel || tx.paymentMethod || '—'}</td>
                    <td>{getStatusBadge(tx.status)}</td>
                    <td className="date-cell">
                      <Calendar size={12} className="inline mr-1" />
                      {tx.createdAt
                        ? new Date(tx.createdAt).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td>
                      <button 
                        className="action-btn view"
                        onClick={() => fetchOrgTransactions(tx.organizationId)}
                      >
                        <Eye size={14} /> View Org
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalElements > 0 && (
          <div className="pagination-container" style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderTop: '1px solid #E2E8F0',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div className="pagination-info" style={{ fontSize: '13px', color: '#64748B' }}>
              Showing {transactions.length} of {totalElements} transactions
            </div>
            <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                onClick={goToFirstPage}
                disabled={currentPage === 0}
                className="pagination-btn"
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  background: 'white',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 0 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="pagination-btn"
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  background: 'white',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 0 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ChevronLeft size={16} />
              </button>
              
              <span style={{ fontSize: '13px', padding: '0 12px', color: '#0F172A' }}>
                Page {currentPage + 1} of {totalPages}
              </span>
              
              <button
                onClick={goToNextPage}
                disabled={currentPage >= totalPages - 1}
                className="pagination-btn"
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  background: 'white',
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= totalPages - 1 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={goToLastPage}
                disabled={currentPage >= totalPages - 1}
                className="pagination-btn"
                style={{
                  padding: '6px 10px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  background: 'white',
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= totalPages - 1 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <ChevronsRight size={16} />
              </button>

              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  background: 'white',
                  fontSize: '13px',
                  marginLeft: '8px'
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span style={{ fontSize: '12px', color: '#64748B' }}>per page</span>
            </div>
          </div>
        )}
      </Card>

      {/* Organization Transaction Modal */}
      <Modal 
        isOpen={showOrgModal} 
        onClose={() => setShowOrgModal(false)} 
        title="Organization Transactions"
        size="large"
      >
        <div className="org-tx-modal">
          {selectedOrg && (
            <div className="org-tx-header">
              <div className="org-tx-info">
                <div className="org-tx-avatar">
                  <Building size={24} />
                </div>
                <div>
                  <h4>{selectedOrg.name}</h4>
                  <div className="org-tx-details">
                    <span><Mail size={12} /> {selectedOrg.email}</span>
                    {selectedOrg.phoneNumber && (
                      <span><Phone size={12} /> {selectedOrg.phoneNumber}</span>
                    )}
                  </div>
                </div>
              </div>
              <Badge variant={selectedOrg.status === 'ACTIVE' ? 'success' : 'warning'}>
                {selectedOrg.status}
              </Badge>
            </div>
          )}

          {loadingOrgTx ? (
            <div className="admin-loading" style={{ minHeight: '200px' }}>
              <Spinner size="lg" />
            </div>
          ) : orgTransactions.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px' }}>
              <CreditCard size={40} className="empty-icon" />
              <h4>No transactions</h4>
              <p>This organization has no payment transactions</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Type</th>
                    <th>Credits</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orgTransactions.map(tx => (
                    <tr key={tx.id}>
                      <td className="tx-reference">{tx.sourceReference || tx.id?.slice(0, 12)}</td>
                      <td>{getTypeBadge(tx.transactionType)}</td>
                      <td>{tx.credits?.toLocaleString() || '—'}</td>
                      <td className="tx-amount">TZS {Number(tx.amount).toLocaleString()}</td>
                      <td>{getStatusBadge(tx.status)}</td>
                      <td className="date-cell">
                        {tx.createdAt
                          ? new Date(tx.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowOrgModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Registration Fees Modal */}
      <Modal
        isOpen={showRegistrationFees}
        onClose={() => setShowRegistrationFees(false)}
        title="Registration Fee Payments"
        size="large"
      >
        <div className="reg-fees-modal">
          <p style={{ color: '#64748B', marginBottom: '20px' }}>
            All organizations that have paid the registration fee
          </p>

          {loadingRegFees ? (
            <div className="admin-loading" style={{ minHeight: '150px' }}>
              <Spinner size="lg" />
            </div>
          ) : registrationFees.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px' }}>
              <Users size={40} className="empty-icon" />
              <h4>No registration fees</h4>
              <p>No organizations have paid the registration fee yet</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Organization</th>
                    <th>Email</th>
                    <th>Reference</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {registrationFees.map(fee => (
                    <tr key={fee.id}>
                      <td>
                        <div className="org-name">{fee.organizationName || 'Unknown'}</div>
                      </td>
                      <td>{fee.organizationEmail || '—'}</td>
                      <td className="tx-reference">{fee.sourceReference || fee.id?.slice(0, 12)}</td>
                      <td className="tx-amount">TZS {Number(fee.amount).toLocaleString()}</td>
                      <td className="date-cell">
                        {fee.createdAt
                          ? new Date(fee.createdAt).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="modal-actions">
            <Button variant="ghost" onClick={() => setShowRegistrationFees(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPayments;